/**
 * Entity kinds — bundle-portable configuration entities for v1.
 *
 * Per ADR-0014 CRIT-3: v1 ships with 6 foundation entity kinds. The 19
 * additional kinds proposed in IMPORT-EXPORT-SPEC v1 are deferred to v1.1
 * (their DB tables remain, just not bundle-portable yet).
 *
 * Why these 6:
 *   These are the "AI catalog" entities — the things a customer or Viaanix
 *   ships as a coherent pack (e.g., a "manufacturing maintenance bundle"
 *   would contain Tools + MCP servers + Skills + Prompts + Agents + a
 *   Knowledge pack of SOPs/manuals). All other configurable entities are
 *   either:
 *     - Tenant-scoped policy (Roles, Brand, Toggles) → applied at tenant
 *       provisioning, not bundled into customer-shippable packs.
 *     - Operational state (Audit, POA&M, Evidence) → tenant data, not
 *       portable config.
 *     - Future module entities (Workflows, LoRA adapters) → ship after
 *       their owning module lands in Phase C.
 *
 * Wire protocol: bundles export each entity as a JSON file inside a tar +
 * Ed25519-signed manifest (per IMPORT-EXPORT-SPEC §6). Each file's `kind`
 * field MUST be one of the keys below. Unknown kinds cause import to
 * abort with `BUNDLE_INVALID_KIND`.
 */

import { z } from 'zod';

/** v1 entity kinds — bundle-portable. */
export const EntityKindV1Schema = z.enum([
  'tool',                  // single MCP tool definition
  'mcp_server',            // a complete MCP server (tier 2/3, Docker)
  'skill',                 // reusable agent skill (composes tools + prompts)
  'prompt_template',       // parameterized system / user prompt
  'agent_definition',      // full agent configuration (the 14 builtins ship as these)
  'knowledge_pack',        // versioned document corpus (with sub-kinds, see below)
]);
export type EntityKindV1 = z.infer<typeof EntityKindV1Schema>;

/**
 * Sub-kinds of `knowledge_pack`. A pack is a directory of one or more of
 * these — a manufacturing pack might contain SOPs + safety procedures + a
 * domain glossary. Sub-kinds let the importer route each file to the
 * right ingestion pipeline (parse + chunk + embed).
 */
export const KnowledgePackSubKindSchema = z.enum([
  'document',              // PDF / DOCX / MD — raw documents to ingest
  'glossary',              // domain synonym table (e.g., "LOTO" = "lockout/tagout")
  'taxonomy',              // categorical hierarchy (asset types, departments, etc.)
  'classification_rules',  // sensitivity / export-control labelling rules
  'chunking_profile',      // override defaults for this pack's documents
  'ingestion_metadata',    // pre-computed manifest (provenance, checksums)
]);
export type KnowledgePackSubKind = z.infer<typeof KnowledgePackSubKindSchema>;

/**
 * v1.1+ entity kinds — DEFERRED. Listed here so the runtime can detect
 * deferred-kind import attempts and return a specific error
 * (`BUNDLE_KIND_DEFERRED`) instead of treating them as unknown.
 *
 * When a kind moves from this list to EntityKindV1Schema, its bundle
 * format is locked by an IMPORT-EXPORT-SPEC v1.x amendment ADR.
 */
export const EntityKindDeferredSchema = z.enum([
  'role',                          // RBAC role definition
  'permission_set',                // permission grants
  'brand_theme',                   // tenant branding (logo, colors, copy)
  'feature_toggle_profile',        // feature-flag preset
  'workflow_automation',           // Automate workflow definition
  'playbook',                      // deterministic agent playbook
  'audit_policy',                  // hash-chain + retention rules
  'retention_policy',              // data retention schedule
  'webhook_endpoint',              // outbound webhook config
  'notification_template',         // email / Slack / Teams template
  'domain_synonym_table',          // global synonyms (vs pack-scoped glossary)
  'idp_config',                    // SSO identity provider config
  'cron_job',                      // scheduled-agent cron config
  'compliance_framework_customization', // tenant-specific framework overrides
  'poam_item',                     // Plan of Action & Milestones entry
  'evidence_pack',                 // compliance evidence bundle
  'guardrail_policy',              // PII / classification / safety rules
  'model_registry_entry',          // approved model + version pin
  'lora_adapter',                  // fine-tuned LoRA weights manifest
]);
export type EntityKindDeferred = z.infer<typeof EntityKindDeferredSchema>;

/**
 * Union of every kind the runtime might encounter on import. Deferred
 * kinds parse but the importer rejects them with a specific error.
 */
export const EntityKindAnySchema = z.union([
  EntityKindV1Schema,
  EntityKindDeferredSchema,
]);
export type EntityKindAny = z.infer<typeof EntityKindAnySchema>;

/** Helpers for the import path. */
export function isV1EntityKind(k: string): k is EntityKindV1 {
  return EntityKindV1Schema.safeParse(k).success;
}
export function isDeferredEntityKind(k: string): k is EntityKindDeferred {
  return EntityKindDeferredSchema.safeParse(k).success;
}

/** Counts — sanity check the lock at module-load time. */
if (EntityKindV1Schema.options.length !== 6) {
  throw new Error(
    `EntityKindV1Schema must have 6 entries (per ADR-0014 CRIT-3), found ${EntityKindV1Schema.options.length}`
  );
}
if (EntityKindDeferredSchema.options.length !== 19) {
  throw new Error(
    `EntityKindDeferredSchema must have 19 entries (per ADR-0014 CRIT-3), found ${EntityKindDeferredSchema.options.length}`
  );
}
